import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  RefreshCw, 
  AlertCircle, 
  X, 
  FileText, 
  Calendar, 
  ChevronRight, 
  MoreHorizontal,
  Hash,
  Search,
  Filter,
  ChevronDown,
  Clock,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DemandeDetail from './DemandeDetail';
import api from '../../api';

const DemandeRefuser = ({ isNavbarOpen }) => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revalidatingId, setRevalidatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Intercepteur pour gérer les erreurs globalement
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        return Promise.reject(error);
      }
    );
    
    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  // Fonction pour récupérer les demandes refusées
  const fetchDemandesRefusees = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/demandes');
      const dataArray = response.data.demandes || [];
      
      // Filtrer les demandes selon les conditions spécifiées
      const demandesRefusees = dataArray.filter(demande => {
        const accepter = String(demande.accepter);
        const confirmationAdmin = Number(demande.confirmation_admin);
        
        return (accepter === '2' && confirmationAdmin === 0) ||
               (accepter === '2' && confirmationAdmin === 2);
      });
      
      setDemandes(demandesRefusees);
      
    } catch (err) {
      console.error('Erreur détaillée:', err);
      
      if (err.code === 'ECONNABORTED') {
        setError('Timeout: Le serveur met trop de temps à répondre.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Erreur réseau: Impossible de se connecter au serveur.');
      } else if (err.response) {
        const status = err.response.status;
        switch (status) {
          case 404:
            setError('Endpoint non trouvé: /api/demandes n\'existe pas sur le serveur.');
            break;
          case 500:
            setError('Erreur serveur interne: Problème côté backend.');
            break;
          case 403:
            setError('Accès interdit: Vérifiez vos permissions.');
            break;
          case 401:
            setError('Non autorisé: Authentification requise.');
            break;
          default:
            setError(`Erreur HTTP ${status}: ${err.response.data?.message || err.response.statusText}`);
        }
      } else if (err.request) {
        setError('Aucune réponse du serveur. Vérifiez que le serveur backend est démarré.');
      } else {
        setError(`Erreur: ${err.message}`);
      }
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour revalider une demande
  const revaliderDemande = useCallback(async (demandeId) => {
    setRevalidatingId(demandeId);
    setError('');
    
    try {
      await api.post(`/demandes/${demandeId}`, {
        accepter: '3',
        confirmation_admin: '0'
      });
      
      toast.success('Demande revalidée avec succès');
      
      // Supprimer la demande de la liste locale après revalidation
      setDemandes(prevDemandes => 
        prevDemandes.filter(demande => demande.id !== demandeId)
      );
      
    } catch (err) {
      console.error('Erreur revalidation:', err);
      
      if (err.response) {
        const status = err.response.status;
        switch (status) {
          case 404:
            setError(`Demande ${demandeId} non trouvée.`);
            break;
          case 422:
            setError('Données invalides pour la revalidation.');
            break;
          case 500:
            setError('Erreur serveur lors de la revalidation.');
            break;
          default:
            setError(`Erreur lors de la revalidation: ${err.response.data?.message || err.response.statusText}`);
        }
      } else {
        setError('Erreur lors de la revalidation de la demande');
      }
      toast.error('Erreur lors de la revalidation');
    } finally {
      setRevalidatingId(null);
    }
  }, []);

  // Charger les demandes au montage du composant
  useEffect(() => {
    fetchDemandesRefusees();
  }, [fetchDemandesRefusees]);

  // Fonction pour formater la date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  // Fonction pour déterminer le statut d'affichage
  const getStatutAffichage = useCallback((demande) => {
    if (demande.accepter === '2' && demande.confirmation_admin === '0') {
      return { text: 'Refusé - En attente admin', color: 'text-orange-600', bg: 'bg-orange-100' };
    }
    if (demande.accepter === '2' && demande.confirmation_admin === '2') {
      return { text: 'Refusé - Confirmé admin', color: 'text-red-600', bg: 'bg-red-100' };
    }
    return { text: 'Statut inconnu', color: 'text-gray-500', bg: 'bg-gray-100' };
  }, []);

  // Fonction de tri
  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  }, [sortBy, sortOrder]);

  // Filtrer et trier les demandes
  const filteredDemandes = useMemo(() => {
    return demandes
      .filter(demande => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
          demande.nom?.toLowerCase().includes(searchLower) ||
          demande.id.toString().includes(searchTerm) ||
          demande.content?.toLowerCase().includes(searchLower) ||
          (demande.ticket?.nom && demande.ticket.nom.toLowerCase().includes(searchLower)) ||
          (demande.ticket?.content && demande.ticket.content.toLowerCase().includes(searchLower))
        );
      })
      .sort((a, b) => {
        let aValue, bValue;
        switch (sortBy) {
          case 'id':
            aValue = a.id;
            bValue = b.id;
            break;
          case 'nom':
            aValue = a.nom || a.ticket?.nom || '';
            bValue = b.nom || b.ticket?.nom || '';
            break;
          case 'date':
            aValue = new Date(a.created_at || a.date_creation);
            bValue = new Date(b.created_at || b.date_creation);
            break;
          default:
            aValue = a.id;
            bValue = b.id;
        }

        if (typeof aValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
  }, [demandes, searchTerm, sortBy, sortOrder]);

  // Statistiques
  const stats = useMemo(() => {
    const total = demandes.length;
    const enAttente = demandes.filter(d => d.accepter === '2' && d.confirmation_admin === '0').length;
    const confirme = demandes.filter(d => d.accepter === '2' && d.confirmation_admin === '2').length;
    
    return { total, enAttente, confirme };
  }, [demandes]);

  return (
    <div className={`bg-gray-50 min-h-screen ${isNavbarOpen ? 'pl-64' : 'pl-20'} transition-all duration-300`}>
      <div className="p-4 md:p-6 h-full flex flex-col w-full">
        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Header avec breadcrumb */}
        <div className="mb-4">
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <FileText className="mr-2 h-4 w-4" />
            <span>Gestion Globale</span>
            <span className="mx-2">/</span>
            <span className="font-medium">Demandes Refusées</span>
          </div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">
                Demandes Refusées
              </h1>
              <p className="text-gray-600 text-sm">
                Gérer et revalider les demandes qui ont été refusées
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchDemandesRefusees}
                disabled={loading}
                className="flex items-center px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 shadow-sm text-sm"
              >
                <RefreshCw className={`mr-1 md:mr-2 text-purple-600 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Actualiser</span>
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Total refusées</p>
                <p className="text-xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="p-2 bg-red-100 rounded-full">
                <X className="text-red-600 h-4 w-4" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">En attente admin</p>
                <p className="text-xl font-bold text-orange-600">
                  {stats.enAttente}
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded-full">
                <AlertCircle className="text-orange-600 h-4 w-4" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Refus confirmé</p>
                <p className="text-xl font-bold text-red-600">
                  {stats.confirme}
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-full">
                <X className="text-red-600 h-4 w-4" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filtres et recherche */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 mb-3"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher par titre, description ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Filter className="h-4 w-4" />
                  <span className="hidden md:inline">Trier</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isFilterOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 p-2">
                    <div className="text-xs font-medium text-gray-500 mb-2">Trier par</div>
                    <button
                      onClick={() => {
                        handleSort('id');
                        setIsFilterOpen(false);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm ${sortBy === 'id' ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50'}`}
                    >
                      <span>ID</span>
                      {sortBy === 'id' && (
                        <ChevronDown className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleSort('nom');
                        setIsFilterOpen(false);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm ${sortBy === 'nom' ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50'}`}
                    >
                      <span>Titre</span>
                      {sortBy === 'nom' && (
                        <ChevronDown className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleSort('date');
                        setIsFilterOpen(false);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm ${sortBy === 'date' ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50'}`}
                    >
                      <span>Date</span>
                      {sortBy === 'date' && (
                        <ChevronDown className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                  </div>
                )}
              </div>
              
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Message d'erreur */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r text-sm"
          >
            <div className="flex items-center">
              <AlertCircle className="text-red-500 mr-2 h-4 w-4 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          </motion.div>
        )}

        {/* Tableau principal */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col border border-gray-200 flex-1"
        >
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-gray-600 text-sm">Chargement des demandes...</p>
              </div>
            </div>
          ) : filteredDemandes.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center space-y-3 flex-1">
              <div className="p-3 bg-gray-100 rounded-full">
                <FileText size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                {searchTerm ? 'Aucun résultat trouvé' : 'Aucune demande refusée'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {searchTerm 
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Toutes les demandes ont été traitées ou aucune demande n\'a été refusée'
                }
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          ) : (
            <>
              {/* En-tête du tableau */}
              <div className="bg-gray-50 text-gray-700 sticky top-0 z-10 border-b border-gray-200 hidden md:grid md:grid-cols-12 px-4 py-2.5 font-medium text-xs uppercase tracking-wider">
                <div 
                  className="md:col-span-1 flex items-center cursor-pointer hover:text-purple-600 transition-colors group"
                  onClick={() => handleSort('id')}
                >
                  <Hash className="mr-1 group-hover:scale-110 transition-transform h-3 w-3" />
                  <span>ID</span>
                  {sortBy === 'id' && (
                    <ChevronDown className={`ml-1 transition-transform h-3 w-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                  )}
                </div>
                <div 
                  className="md:col-span-4 flex items-center cursor-pointer hover:text-purple-600 transition-colors group"
                  onClick={() => handleSort('nom')}
                >
                  <FileText className="mr-1 group-hover:scale-110 transition-transform h-3 w-3" />
                  <span>Titre</span>
                  {sortBy === 'nom' && (
                    <ChevronDown className={`ml-1 transition-transform h-3 w-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                  )}
                </div>
                <div className="md:col-span-2 flex items-center justify-center">
                  <span>Statut</span>
                </div>
                <div 
                  className="md:col-span-2 flex items-center cursor-pointer hover:text-purple-600 transition-colors group"
                  onClick={() => handleSort('date')}
                >
                  <Calendar className="mr-1 group-hover:scale-110 transition-transform h-3 w-3" />
                  <span>Date</span>
                  {sortBy === 'date' && (
                    <ChevronDown className={`ml-1 transition-transform h-3 w-3 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
                  )}
                </div>
                <div className="md:col-span-3 flex items-center justify-center">
                  <span>Actions</span>
                </div>
              </div>

              {/* Corps du tableau */}
              <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
                {filteredDemandes.map((demande, index) => {
                  const statut = getStatutAffichage(demande);
                  return (
                    <motion.div 
                      key={demande.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="grid grid-cols-1 md:grid-cols-12 p-3 items-center text-sm hover:bg-gray-50 transition-colors"
                    >
                      {/* Mobile view */}
                      <div className="md:hidden mb-2 pb-2 border-b border-gray-100">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                              #{demande.id}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${statut.bg} ${statut.color}`}>
                              {statut.text.split(' - ')[0]}
                            </span>
                          </div>
                          <button 
                            onClick={() => setSelectedDemande(demande)}
                            className="text-gray-400 hover:text-purple-600 transition-colors"
                            title="Voir détails"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <h3 className="font-medium text-gray-900 mt-1 truncate">
                          {demande.ticket?.nom || demande.nom || 'Sans titre'}
                        </h3>
                        
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(demande.created_at || demande.date_creation)}
                        </div>
                      </div>
                      
                      {/* Desktop view */}
                      {/* Colonne ID */}
                      <div className="hidden md:flex md:col-span-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          #{demande.id}
                        </span>
                      </div>
                      
                      {/* Colonne Titre */}
                      <div className="hidden md:flex md:col-span-4 text-gray-800 pr-2">
                        <div className="flex items-center group truncate">
                          <div className="min-w-0 truncate">
                            <p className="truncate font-medium" title={demande.ticket?.nom || demande.nom}>
                              {demande.ticket?.nom || demande.nom || 'Sans titre'}
                            </p>
                            <p className="truncate text-xs text-gray-500 mt-0.5">
                              {demande.ticket?.content ? `${demande.ticket.content.substring(0, 60)}...` : 'Aucune description'}
                            </p>
                          </div>
                          <button 
                            onClick={() => setSelectedDemande(demande)}
                            className="ml-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-purple-600 transition-all"
                            title="Voir détails"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Colonne Statut */}
                      <div className="hidden md:flex md:col-span-2 justify-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statut.bg} ${statut.color}`}>
                          {statut.text}
                        </span>
                      </div>
                      
                      {/* Colonne Date */}
                      <div className="hidden md:flex md:col-span-2 justify-center">
                        <div className="flex items-center text-gray-600 text-xs">
                          <Calendar className="text-gray-400 mr-1 h-3 w-3" />
                          {formatDate(demande.created_at || demande.date_creation)}
                        </div>
                      </div>
                      
                      {/* Colonne Actions */}
                      <div className="md:col-span-3 flex justify-end md:justify-center">
                        <button
                          onClick={() => revaliderDemande(demande.id)}
                          disabled={revalidatingId === demande.id}
                          className={`flex items-center gap-1 md:gap-2 px-3 py-1.5 text-white rounded-lg transition-all shadow-sm hover:shadow-md text-xs ${
                            revalidatingId === demande.id
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          {revalidatingId === demande.id ? (
                            <>
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              <span className="hidden md:inline">Revalidation...</span>
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-3 w-3" />
                              <span className="hidden md:inline">Revalider</span>
                            </>
                          )}
                        </button>
                        
                        <button 
                          onClick={() => setSelectedDemande(demande)}
                          className="ml-2 p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors md:hidden"
                          title="Voir détails"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer du tableau */}
              <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center text-xs text-gray-600">
                  Affichage de <span className="font-medium text-purple-600 mx-1">1-{filteredDemandes.length}</span> sur {demandes.length} demandes
                </div>
                
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={fetchDemandesRefusees}
                    className="flex items-center text-xs text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" />
                    Actualiser
                  </button>
                  <div className="flex items-center bg-white px-2 py-1 rounded-full border border-gray-200 shadow-sm text-xs text-gray-600">
                    <Clock className="text-gray-400 mr-1 h-3 w-3" />
                    MAJ: {new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>

        {/* Modal de détail */}
        <AnimatePresence>
          {selectedDemande && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedDemande(null)}
            >
              <motion.div 
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', damping: 25 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Détails de la demande #{selectedDemande.id}</h3>
                      <p className="text-sm text-gray-500">
                        Créée le {formatDate(selectedDemande.created_at || selectedDemande.date_creation)}
                      </p>
                    </div>
                    <button 
                      onClick={() => setSelectedDemande(null)}
                      className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <X className="text-gray-500 h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-700">Titre</h4>
                      <p className="mt-1 text-gray-900">{selectedDemande.ticket?.nom || selectedDemande.nom || 'Non spécifié'}</p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700">Description</h4>
                      <p className="mt-1 text-gray-900 whitespace-pre-line">
                        {selectedDemande.ticket?.content || selectedDemande.content || 'Aucune description fournie'}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700">Statut</h4>
                      <p className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getStatutAffichage(selectedDemande).color === 'text-orange-600' 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {getStatutAffichage(selectedDemande).text}
                        </span>
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700">Cause du rejet</h4>
                      <p className="mt-1 text-gray-900">
                        {selectedDemande.cause_reject || 'Non spécifiée'}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          revaliderDemande(selectedDemande.id);
                          setSelectedDemande(null);
                        }}
                        disabled={revalidatingId === selectedDemande.id}
                        className={`flex items-center justify-center gap-2 px-4 py-2 w-full text-white rounded-lg transition-all shadow-sm hover:shadow-md ${
                          revalidatingId === selectedDemande.id
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-purple-600 hover:bg-purple-700'
                        }`}
                      >
                        {revalidatingId === selectedDemande.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Revalidation en cours...
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4" />
                            Revalider cette demande
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DemandeRefuser;